import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { NAV_LABELS } from '@/lib/vocabulary/securityTerminology';
import { Input } from '@/components/ui/input';
import { demoDataProvider } from '@/demo/demoProvider';
import type { DemoDomain, DemoDomainLog } from '@/demo/demoFixtures';
import { useDemoMode } from '@/demo/DemoContext';

const statusBadge = (status?: string) => {
  switch (status) {
    case 'critical':
      return { label: 'Critical', tone: 'danger' as const };
    case 'warn':
      return { label: 'Warning', tone: 'warning' as const };
    case 'resolved':
    case 'ok':
      return { label: 'Resolved', tone: 'success' as const };
    default:
      return { label: 'Info', tone: 'neutral' as const };
  }
};

export default function DemoDomainLogs() {
  const { domain } = useOutletContext<{ domain: DemoDomain }>();
  const { demoEvents } = useDemoMode();
  const [filters, setFilters] = useState({
    owaspId: '',
    status: '',
    controlKey: '',
    enforced: '',
    search: '',
  });

  const [logItems, setLogItems] = useState<DemoDomainLog[]>([]);

  useEffect(() => {
    let mounted = true;
    demoDataProvider.getDomainLogs(domain.site_id).then((items) => {
      if (!mounted) return;
      const scopedEvents = demoEvents.filter((event) => event.site_id === domain.site_id);
      setLogItems([...scopedEvents, ...items]);
    });
    return () => {
      mounted = false;
    };
  }, [demoEvents, domain.site_id]);

  const filteredLogs = useMemo(() => {
    return logItems.filter((log) => {
      if (filters.owaspId && log.owasp_id !== filters.owaspId) return false;
      if (filters.status && log.status !== filters.status) return false;
      if (filters.controlKey && log.control_key !== filters.controlKey) return false;
      if (filters.enforced) {
        const enforcedValue = filters.enforced === 'true';
        if (Boolean(log.enforced) !== enforcedValue) return false;
      }
      if (filters.search) {
        const haystack = `${log.summary ?? ''} ${log.event_type ?? ''} ${log.control_key ?? ''}`.toLowerCase();
        if (!haystack.includes(filters.search.toLowerCase())) return false;
      }
      return true;
    });
  }, [filters, logItems]);

  const owaspIds = useMemo(() => {
    const set = new Set<string>();
    logItems.forEach((log) => log.owasp_id && set.add(log.owasp_id));
    return Array.from(set);
  }, [logItems]);

  const controlKeys = useMemo(() => {
    const set = new Set<string>();
    logItems.forEach((log) => log.control_key && set.add(log.control_key));
    return Array.from(set);
  }, [logItems]);

  const statuses = useMemo(() => {
    const set = new Set<string>();
    logItems.forEach((log) => log.status && set.add(log.status));
    return Array.from(set);
  }, [logItems]);

  const categoryLabel = (value?: string | null) => {
    if (!value) return 'Security activity';
    if (value === 'owasp') return 'Protection coverage';
    if (value === 'security_control') return 'Security controls';
    return value;
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
      <CardTitle>{NAV_LABELS.securityActivity} (Demo)</CardTitle>
      <CardDescription>Simulated security activity stream with coverage filters.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Search logs"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            className="w-full md:w-56"
          />
          <select
            value={filters.owaspId}
            onChange={(event) => setFilters((prev) => ({ ...prev, owaspId: event.target.value }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">All coverage mappings</option>
            {owaspIds.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={filters.controlKey}
            onChange={(event) => setFilters((prev) => ({ ...prev, controlKey: event.target.value }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">All controls</option>
            {controlKeys.map((key) => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
          <select
            value={filters.enforced}
            onChange={(event) => setFilters((prev) => ({ ...prev, enforced: event.target.value }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">Enforcement (any)</option>
            <option value="true">Enforced</option>
            <option value="false">Not enforced</option>
          </select>
        </div>

        <div className="space-y-2 max-h-[520px] overflow-auto pr-2">
          {filteredLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No demo log records for the current filters.</p>
          ) : filteredLogs.map((log) => {
            const badge = statusBadge(log.status);
            return (
              <div key={log.id} className="p-3 rounded border border-border/40 bg-muted/30 text-xs space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono break-all">{log.owasp_id || categoryLabel(log.category)}</span>
                  <span>{new Date(log.created_at).toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={badge.tone}>{badge.label}</Badge>
                  {log.enforced !== undefined && (
                    <Badge variant={log.enforced ? 'success' : 'neutral'}>
                      {log.enforced ? 'Enforced' : 'Not enforced'}
                    </Badge>
                  )}
                </div>
                <div>Control: {log.control_key || 'n/a'}</div>
                <div>Event: {log.event_type}</div>
                <div>Severity: {log.severity}</div>
                <div>Domain: {log.domain}</div>
                {log.summary && (
                  <div className="text-muted-foreground break-all">{log.summary}</div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
